/**
 * OrthostaticPanel — iter1057 — ортостатичен тест с водене стъпка по стъпка:
 * легнал → 1 мин прав → 3 мин прав, с автоматичен таймер и разчитане.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ArrowRight, Loader2, Play, StretchHorizontal } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["supine", "stand1", "stand3"];
const STEP_LABEL = { supine: "Легнал (изходна точка)", stand1: "1-ва минута прав", stand3: "3-та минута прав" };

export const OrthostaticPanel = ({ isDark, onSaved }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("idle");
  const [vals, setVals] = useState({});
  const [seconds, setSeconds] = useState(0);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const load = () => axios.get(`${API}/cardio/orthostatic`)
    .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);
  useEffect(() => () => clearInterval(timer.current), []);

  const startTimer = (target, next) => {
    setPhase("waiting");
    setSeconds(target);
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(timer.current); setPhase(next); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      STEPS.forEach((st) => ["sbp", "dbp", "hr"].forEach((k) => {
        const v = vals[`${st}_${k}`];
        if (v !== "" && v !== undefined) payload[`${st}_${k}`] = parseFloat(v);
      }));
      payload.symptoms = vals.symptoms || [];
      const { data } = await axios.post(`${API}/cardio/orthostatic`, payload);
      setData(data);
      setPhase("idle");
      setVals({});
      toast.success("Тестът е записан.");
      onSaved?.();
    } catch {
      toast.error("Записът не мина.");
    } finally {
      setSaving(false);
    }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  const inputCls = `px-2 py-1.5 rounded-md border text-sm w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`;

  const triple = (step) => (
    <div className="grid grid-cols-3 gap-2" data-testid={`ortho-inputs-${step}`}>
      {[["sbp", "Систолно"], ["dbp", "Диастолно"], ["hr", "Пулс"]].map(([k, label]) => (
        <label key={k} className="flex flex-col gap-1">
          <span className="text-[10px] opacity-70">{label}</span>
          <input type="number" className={inputCls} value={vals[`${step}_${k}`] || ""}
                 data-testid={`ortho-input-${step}-${k}`}
                 onChange={(e) => setVals({ ...vals, [`${step}_${k}`]: e.target.value })} />
        </label>
      ))}
    </div>
  );

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${card} flex justify-center`} data-testid="ortho-loading">
      <Loader2 className="w-5 h-5 animate-spin text-red-500" /></div>;
  }
  if (!data) return null;
  const v = data.latest?.verdict;

  return (
    <div className={`p-5 rounded-2xl border ${card}`} data-testid="orthostatic-panel">
      <div className="flex items-center gap-2 mb-1">
        <StretchHorizontal className="w-4 h-4 text-red-500" />
        <h2 className="font-bold text-sm">Ортостатичен тест</h2>
      </div>
      <p className="text-[11px] opacity-75 leading-relaxed mb-2">{data.why_bg}</p>

      <ol className={`p-2.5 rounded-lg text-[10px] mb-2 space-y-0.5 ${box}`} data-testid="ortho-instructions">
        {data.instructions_bg.map((s, i) => <li key={i}>{i + 1}. {s}</li>)}
      </ol>

      {phase === "idle" && (
        <button onClick={() => setPhase("supine")} data-testid="ortho-start"
                className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold flex items-center justify-center gap-1">
          <Play className="w-3.5 h-3.5" /> Започни теста
        </button>
      )}

      {phase === "supine" && (
        <div className="space-y-2" data-testid="ortho-phase-supine">
          <div className="text-[10px] font-bold opacity-80">{STEP_LABEL.supine} — след 5 минути спокойно лежане</div>
          {triple("supine")}
          <button onClick={() => startTimer(60, "stand1")} data-testid="ortho-stand"
                  className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold">
            Изправих се — старт на 1-вата минута
          </button>
        </div>
      )}

      {phase === "waiting" && (
        <div className="text-center py-3" data-testid="ortho-phase-waiting">
          <div className="text-4xl font-extrabold tabular-nums" data-testid="ortho-timer">{seconds}</div>
          <p className="text-[10px] opacity-70 mt-1">Останете прави, без да се облягате.</p>
        </div>
      )}

      {phase === "stand1" && (
        <div className="space-y-2" data-testid="ortho-phase-stand1">
          <div className="text-[10px] font-bold opacity-80">{STEP_LABEL.stand1}</div>
          {triple("stand1")}
          <button onClick={() => startTimer(120, "stand3")} data-testid="ortho-wait-3"
                  className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold">
            Записах — чакам до 3-тата минута
          </button>
        </div>
      )}

      {phase === "stand3" && (
        <div className="space-y-2" data-testid="ortho-phase-stand3">
          <div className="text-[10px] font-bold opacity-80">{STEP_LABEL.stand3}</div>
          {triple("stand3")}
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ortho-symptoms">
            {data.symptoms.map((s) => {
              const on = (vals.symptoms || []).includes(s.key);
              return (
                <button key={s.key} data-testid={`ortho-symptom-${s.key}`}
                        onClick={() => {
                          const cur = vals.symptoms || [];
                          setVals({ ...vals, symptoms: on ? cur.filter((x) => x !== s.key) : [...cur, s.key] });
                        }}
                        className={`text-[10px] px-2 py-1 rounded-lg border ${on
                          ? "bg-red-600 text-white border-red-600"
                          : isDark ? "border-slate-700" : "border-slate-300"}`}>
                  {s.label_bg}
                </button>
              );
            })}
          </div>
          <button onClick={save} disabled={saving} data-testid="ortho-save"
                  className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Записвам…" : "Запиши резултата"}
          </button>
        </div>
      )}

      {v && (
        <div className={`mt-3 p-3 rounded-xl ${v.abnormal
          ? (isDark ? "bg-rose-900/25 border border-rose-500/40" : "bg-rose-50 border border-rose-200")
          : (isDark ? "bg-emerald-900/20 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200")}`}
             data-testid="ortho-verdict">
          <div className="text-xs font-bold" data-testid="ortho-verdict-label">{v.label_bg}</div>
          <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{v.text_bg}</div>
          {v.rows?.length > 0 && (
            <div className="mt-2 space-y-1">
              {v.rows.map((r, i) => (
                <div key={i} className={`text-[10px] p-1.5 rounded-md flex justify-between gap-2 ${box}`}>
                  <span className="opacity-60">{r.label_bg}</span>
                  <span className="tabular-nums">
                    {r.sbp ?? "—"}/{r.dbp ?? "—"} · пулс {r.hr ?? "—"}
                    {r.sbp_drop != null && ` · спад ${r.sbp_drop > 0 ? "−" : "+"}${Math.abs(r.sbp_drop)}`}
                    {r.hr_rise != null && ` · пулс ${r.hr_rise > 0 ? "+" : ""}${r.hr_rise}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ul className="text-[10px] opacity-65 mt-2 space-y-0.5" data-testid="ortho-rules">
        {data.rules_bg.map((r, i) => <li key={i}>• {r}</li>)}
      </ul>

      {data.links?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ortho-links">
          {data.links.map((l, i) => (
            <button key={i} onClick={() => navigate(l.route)} data-testid={`ortho-link-${i}`}
                    className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
              {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrthostaticPanel;
